import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiHome, FiDownload, FiMail, FiPhone } = FiIcons;

function ApplicationSuccess() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-agric-primary via-agric-secondary to-agric-accent flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-2xl w-full text-center"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          className="bg-green-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8"
        >
          <SafeIcon icon={FiCheckCircle} className="w-12 h-12 text-green-600" />
        </motion.div>

        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-agric-primary mb-4">
            Application Submitted Successfully!
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Thank you for choosing Agric Bank Ghana. Your account application has been received 
            and is currently being processed.
          </p>
        </motion.div>

        {/* Application Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gray-50 rounded-lg p-6 mb-8"
        >
          <h3 className="text-xl font-semibold text-agric-primary mb-4">Application Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            <div>
              <p className="text-sm text-gray-600">Application ID</p>
              <p className="font-semibold text-agric-primary">ADB-{Date.now().toString().slice(-8)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Submitted On</p>
              <p className="font-semibold text-agric-primary">{new Date().toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Processing Time</p>
              <p className="font-semibold text-agric-primary">2-3 Business Days</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="font-semibold text-orange-600">Under Review</p>
            </div>
          </div>
        </motion.div>

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="text-left mb-8"
        >
          <h3 className="text-xl font-semibold text-agric-primary mb-4">What Happens Next?</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="bg-agric-primary/10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-agric-primary font-semibold text-sm">1</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">Document Verification</p>
                <p className="text-sm text-gray-600">Our team will verify the information and documents provided</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-agric-primary/10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-agric-primary font-semibold text-sm">2</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">Approval Notification</p>
                <p className="text-sm text-gray-600">You'll receive an email/SMS with the approval status</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-agric-primary/10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-agric-primary font-semibold text-sm">3</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">Account Activation</p>
                <p className="text-sm text-gray-600">Visit any branch to complete account activation and collect your debit card</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center gap-2 px-8 py-3 bg-agric-primary text-white rounded-lg font-medium hover:bg-agric-secondary transition-all duration-300 transform hover:scale-105"
          >
            <SafeIcon icon={FiHome} className="w-5 h-5" />
            Back to Home
          </button>
          
          <button className="flex items-center justify-center gap-2 px-8 py-3 border-2 border-agric-primary text-agric-primary rounded-lg font-medium hover:bg-agric-primary hover:text-white transition-all duration-300">
            <SafeIcon icon={FiDownload} className="w-5 h-5" />
            Download Receipt
          </button>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 }}
          className="mt-8 pt-8 border-t border-gray-200"
        >
          <p className="text-gray-600 mb-4">Need help or have questions?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="flex items-center gap-2 text-agric-primary">
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>+233(0)302-210210</span>
            </div>
            <div className="flex items-center gap-2 text-agric-primary">
              <SafeIcon icon={FiMail} className="w-5 h-5" />
              <span>customercare@agricbank.com</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default ApplicationSuccess;